import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router";
import { api } from "../api.ts";
import { dateTime, minutes, money, periodName, REASON, SOURCE, STRENGTH } from "../format.ts";
import { canEdit, useMe } from "../session.ts";
import {
  Badge,
  Button,
  ButtonLink,
  Card,
  Empty,
  ErrorText,
  LoadError,
  Loading,
  Notice,
  Page,
  Table,
  Td,
} from "../ui.tsx";

export interface Explanation {
  target: string;
  availability: string;
  periodMinutes: string;
  excludedMinutes: string;
  eligibleMinutes: string;
  downtimeMinutes: string;
  reliesOn: string | null;
  incidents: {
    itemId: string;
    title: string;
    source: string;
    strength: string;
    reason: string;
    start: string;
    end: string;
    minutesInPeriod: string;
    countedMinutes: string;
    revision: number | null;
  }[];
  excluded: { itemId: string; reason: string; start: string; end: string; clause: string | null }[];
  belowMinimum: { start: string; end: string; minutes: string }[];
  tier: { belowPercent: string; inclusive: boolean; creditPercent: string } | null;
  steps: { step: string; text: string; cents: string }[];
  feeNeeded: boolean;
  citations: Record<string, string>;
  support?: {
    onTime: number;
    breached: number;
    pending: number;
    attainmentPercent: number | null;
    met: boolean;
  } | null;
  notifications?: { id: string; title: string; dueAt: string; status: string }[] | null;
}

/** The arithmetic behind one month: every interval, every exclusion, and the credit. */
export function Breakdown({
  e,
  met,
  provisional,
  creditCents,
  currency,
}: {
  e: Explanation;
  met: boolean;
  provisional: boolean;
  creditCents: string | null;
  currency: string | null;
}) {
  return (
    <>
      <div className="grid gap-4 sm:grid-cols-3">
        <div className="rounded-control border border-line bg-surface p-5">
          <div className="eyebrow">Uptime</div>
          <div className="tabular mt-1 font-display text-2xl font-bold">{e.availability}%</div>
          <div className="mt-1 text-sm text-muted">
            Promised {e.target}% ·{" "}
            {provisional ? (
              <Badge>In progress</Badge>
            ) : met ? (
              <Badge tone="ok">Met</Badge>
            ) : (
              <Badge tone="danger">Missed</Badge>
            )}
          </div>
        </div>
        <div className="rounded-control border border-line bg-surface p-5">
          <div className="eyebrow">Downtime that counts</div>
          <div className="tabular mt-1 font-display text-2xl font-bold">
            {minutes(e.downtimeMinutes)}
          </div>
          <div className="mt-1 text-sm text-muted">
            of {Number(e.eligibleMinutes).toLocaleString()} minutes that count
          </div>
        </div>
        <div className="rounded-control border border-line bg-surface p-5">
          <div className="eyebrow">Credit owed</div>
          <div className="tabular mt-1 font-display text-2xl font-bold text-accent">
            {e.feeNeeded ? "Fee needed" : money(creditCents ?? "0", currency ?? "USD")}
          </div>
          {e.reliesOn && (
            <div className="mt-1 text-sm text-muted">
              Proof: {STRENGTH[e.reliesOn]?.toLowerCase()}
            </div>
          )}
        </div>
      </div>

      <Card
        title="Downtime"
        description="Each outage and the minutes it counts for. Overlapping reports are counted once."
        flush
      >
        {e.incidents.length === 0 ? (
          <Empty title="No downtime this month." />
        ) : (
          <Table head={["What happened", "When (UTC)", "Source", "Counted"]}>
            {e.incidents.map((i) => (
              <tr key={`${i.itemId}:${i.reason}`}>
                <Td>
                  <div className="font-medium">{i.title || REASON[i.reason]}</div>
                  <div className="text-xs text-muted">
                    {REASON[i.reason]}
                    {i.revision ? ` · counted at the vendor's reading ${i.revision}` : ""}
                  </div>
                </Td>
                <Td className="whitespace-nowrap">
                  {dateTime(i.start)}
                  <div className="text-xs text-muted">to {dateTime(i.end)}</div>
                </Td>
                <Td>
                  {SOURCE[i.source]}
                  <div className="text-xs text-muted">{STRENGTH[i.strength]}</div>
                </Td>
                <Td className="tabular whitespace-nowrap">
                  {minutes(i.countedMinutes)}
                  {Number(i.countedMinutes) < Number(i.minutesInPeriod) && (
                    <div className="text-xs text-muted">of {minutes(i.minutesInPeriod)}</div>
                  )}
                </Td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      {(e.excluded.length > 0 || e.belowMinimum.length > 0) && (
        <Card title="Not counted" description="Time the contract excludes, with its wording." flush>
          <Table head={["Why", "When (UTC)", "Contract"]}>
            {e.excluded.map((x) => (
              <tr key={`${x.itemId}:${x.start}`}>
                <Td>{REASON[x.reason]}</Td>
                <Td className="whitespace-nowrap">
                  {dateTime(x.start)} <span className="text-muted">to</span> {dateTime(x.end)}
                </Td>
                <Td className="text-muted">{x.clause ? `“${x.clause}”` : "—"}</Td>
              </tr>
            ))}
            {e.belowMinimum.map((b) => (
              <tr key={b.start}>
                <Td>Shorter than the contract's minimum ({minutes(b.minutes)})</Td>
                <Td className="whitespace-nowrap">{dateTime(b.start)}</Td>
                <Td className="text-muted">
                  {e.citations.downtime ? `“${e.citations.downtime}”` : "—"}
                </Td>
              </tr>
            ))}
          </Table>
        </Card>
      )}

      {(e.support || (e.notifications && e.notifications.length > 0)) && (
        <Card
          title="Support and notifications"
          description="Measured for the record and your scorecard; credits come from availability."
        >
          <div className="flex flex-col gap-2 text-sm">
            {e.support && (
              <p>
                First responses: {e.support.onTime} on time, {e.support.breached} late
                {e.support.pending ? `, ${e.support.pending} waiting` : ""}.{" "}
                {e.support.attainmentPercent !== null && (
                  <Badge tone={e.support.met ? "ok" : "danger"}>
                    {e.support.attainmentPercent.toFixed(1)}% on time
                  </Badge>
                )}
              </p>
            )}
            {e.notifications?.map((n) => (
              <p key={n.id}>
                {n.title}: due {dateTime(n.dueAt)} ·{" "}
                {n.status === "met" ? (
                  <Badge tone="ok">Told on time</Badge>
                ) : n.status === "missed" ? (
                  <Badge tone="danger">Told late</Badge>
                ) : (
                  <Badge>Waiting</Badge>
                )}
              </p>
            ))}
          </div>
        </Card>
      )}

      <Card title="How we worked it out">
        <ol className="tabular flex flex-col gap-1.5 text-sm">
          <li>
            ({Number(e.eligibleMinutes).toLocaleString()} − {e.downtimeMinutes}) ÷{" "}
            {Number(e.eligibleMinutes).toLocaleString()} = {e.availability}%
          </li>
          {e.steps.map((s) => (
            <li key={s.step}>{s.text}</li>
          ))}
          {e.feeNeeded && (
            <li className="text-warn">
              Add the fee you pay for this service and ninekeep will work out the credit.
            </li>
          )}
        </ol>
        {Object.keys(e.citations).length > 0 && (
          <details className="mt-4 text-sm">
            <summary className="cursor-pointer text-muted">The contract's words</summary>
            <dl className="mt-2 flex flex-col gap-2">
              {Object.entries(e.citations).map(([k, v]) => (
                <div key={k}>
                  <dt className="text-xs text-muted capitalize">{k}</dt>
                  <dd className="whitespace-pre-wrap">“{v}”</dd>
                </div>
              ))}
            </dl>
          </details>
        )}
      </Card>
    </>
  );
}

export function PeriodPage() {
  const { id } = useParams();
  const { data, error, isLoading } = useQuery({
    queryKey: ["period", id],
    queryFn: () =>
      api.get<{
        period: {
          vendor: string;
          vendor_id: string;
          service: string;
          period_key: string;
          claim_id: string | null;
          met: boolean;
          provisional: boolean;
          credit_cents: string | null;
          currency: string | null;
          explanation: Explanation;
        };
      }>(`/periods/${id}`),
  });
  if (isLoading) return <Loading />;
  if (error || !data)
    return (
      <Page help="measuring" title="Month" back={{ to: "/vendors", label: "Vendors" }}>
        <LoadError what="month" error={error} back={{ to: "/vendors", label: "Vendors" }} />
      </Page>
    );
  const p = data.period;
  return (
    <Page
      help="measuring"
      title={`${p.vendor}: ${periodName(p.period_key, true)}`}
      lead={`How ninekeep measured ${p.service} this month.`}
      back={{ to: `/vendors/${p.vendor_id}?tab=months`, label: p.vendor }}
      actions={p.claim_id && <ButtonLink to={`/claims/${p.claim_id}`}>Open the claim</ButtonLink>}
    >
      <UnverifiedMaintenance vendorId={p.vendor_id} periodId={id!} e={p.explanation} />
      <Breakdown
        e={p.explanation}
        met={p.met}
        provisional={p.provisional}
        creditCents={p.credit_cents}
        currency={p.currency}
      />
    </Page>
  );
}

/**
 * Maintenance counted because ninekeep couldn't confirm its notice (it first saw the slot after it
 * began). An admin who trusts the vendor's stated notice can leave it out of the downtime.
 */
function UnverifiedMaintenance({
  vendorId,
  periodId,
  e,
}: {
  vendorId: string;
  periodId: string;
  e: Explanation;
}) {
  const { data: me } = useMe();
  const qc = useQueryClient();
  const flagged = e.incidents.filter((i) => i.reason === "maintenance_notice_unverified");
  const leave = useMutation({
    mutationFn: () =>
      api.post(`/vendors/${vendorId}/maintenance/leave-out`, {
        itemIds: [...new Set(flagged.map((i) => i.itemId))],
      }),
    onSuccess: () => {
      setTimeout(() => qc.invalidateQueries({ queryKey: ["period", periodId] }), 1500);
    },
  });
  if (flagged.length === 0) return null;
  const total = flagged.reduce((n, i) => n + Number(i.countedMinutes), 0);
  return (
    <Notice tone="accent">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <span>
          {minutes(String(total))} of maintenance count as downtime because ninekeep couldn't
          confirm the vendor gave notice: it first saw{" "}
          {flagged.length === 1 ? "the slot" : "these slots"} after{" "}
          {flagged.length === 1 ? "it" : "they"} began.
          {canEdit(me, "admin")
            ? " If you know notice was given, leave them out."
            : " An admin can leave them out if notice was given."}
        </span>
        {canEdit(me, "admin") && (
          <Button busy={leave.isPending} disabled={leave.isSuccess} onClick={() => leave.mutate()}>
            {leave.isSuccess ? "Re-measuring…" : "Leave these out"}
          </Button>
        )}
      </div>
      <ErrorText error={leave.error} />
    </Notice>
  );
}
